import { createEffect, createMemo, createSelector, createSignal, For, onCleanup, onMount, Show } from "solid-js";
import Loading from "../../../../../components/utilities/Loading";
import { apiEndpoints } from "@config/env";
import { persistantStore } from "../../../../../store/persistantStore";
import { getData, setupMediaContainerScaling } from "../../../../../utils/global";
import { logPageLoad } from "../../../../../utils/logging";
import { debug as logDebug, error as logError } from "../../../../../utils/console";
import {
  CREW_HOUR_SEATS,
  crewHourSeatsFromTags,
  summarizeCrewHours,
  type CrewHourEvent,
  type CrewHourMode,
  type CrewHourSeatKey,
} from "../../../../../utils/crewHours";
import { exportCrewHoursExcel } from "../../../../../utils/crewHoursExcel";

const { selectedProjectId, selectedSourceId, selectedSourceName } = persistantStore;

const MODES: { id: CrewHourMode; label: string }[] = [
  { id: "all", label: "All" },
  { id: "racing", label: "Racing" },
  { id: "training", label: "Training" },
];

function formatHours(hours: number): string {
  if (!Number.isFinite(hours) || hours <= 0) return "";
  if (hours < 0.05) return "<0.1";
  return hours.toFixed(1);
}

function calendarDate(raw: unknown): string {
  if (raw == null) return "";
  const text = String(raw).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) return text.slice(0, 10);
  const parsed = new Date(text);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toISOString().slice(0, 10);
}

function asRecord(raw: unknown): Record<string, unknown> {
  if (raw != null && typeof raw === "object" && !Array.isArray(raw)) {
    return raw as Record<string, unknown>;
  }
  return {};
}

function eventSources(tags: Record<string, unknown>): string[] {
  const raw = tags.sources;
  if (Array.isArray(raw)) {
    return raw.map((item) => String(item).trim()).filter(Boolean);
  }
  if (typeof raw === "string" && raw.trim()) return [raw.trim()];
  return [];
}

function raceNumber(tags: Record<string, unknown>): number | null {
  let raw: unknown = tags.Race_number;
  if (raw == null) {
    const races = tags.RACES;
    if (races != null && typeof races === "object" && !Array.isArray(races)) {
      raw = (races as Record<string, unknown>).Race_number;
    }
  }
  const n = Number(raw);
  return Number.isFinite(n) ? Math.trunc(n) : null;
}

function rowsFromPayload(data: unknown): unknown[] {
  if (Array.isArray(data)) return data;
  const record = asRecord(data);
  return Array.isArray(record.rows) ? record.rows : [];
}

function toCrewEvent(raw: unknown): CrewHourEvent | null {
  const row = asRecord(raw);
  const tags = asRecord(row.tags);
  const crewCode = tags.crew_code != null ? String(tags.crew_code).trim() : "";
  if (!crewCode) return null;
  const startMs = new Date(String(row.start_time ?? "")).getTime();
  const endMs = new Date(String(row.end_time ?? "")).getTime();
  const seats = crewHourSeatsFromTags(tags);
  return {
    date: calendarDate(row.date) || calendarDate(row.start_time),
    startMs,
    endMs,
    sources: eventSources(tags),
    crewCode,
    raceNumber: raceNumber(tags),
    seats,
  };
}

export default function CrewHours() {
  const [events, setEvents] = createSignal<CrewHourEvent[]>([]);
  const [loading, setLoading] = createSignal(true);
  const [error, setError] = createSignal("");
  const [startDate, setStartDate] = createSignal("");
  const [endDate, setEndDate] = createSignal("");
  const [mode, setMode] = createSignal<CrewHourMode>("all");
  const isMode = createSelector(mode);
  const [exporting, setExporting] = createSignal(false);
  const [exportError, setExportError] = createSignal("");
  let appliedRangeKey = "";

  onMount(() => {
    logPageLoad("CrewHours.tsx", "CREW HOURS", "Loaded");
    const cleanupScaling = setupMediaContainerScaling({
      logPrefix: "CrewHours",
      scaleToWidth: true,
    });
    onCleanup(() => cleanupScaling());
  });

  createEffect(() => {
    const projectId = selectedProjectId();
    if (!projectId) {
      setEvents([]);
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    setLoading(true);
    setError("");
    const url = `${apiEndpoints.app.userEvents}?project_id=${encodeURIComponent(String(projectId))}&event_type=CREW`;
    getData(url, controller.signal)
      .then((res) => {
        if (controller.signal.aborted) return;
        if (!res.success) {
          setError(res.message || "Could not load crew intervals");
          setEvents([]);
          return;
        }
        const parsed = rowsFromPayload(res.data).map(toCrewEvent).filter((row): row is CrewHourEvent => row != null);
        logDebug("CrewHours: loaded crew intervals", { projectId, count: parsed.length });
        setEvents(parsed);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        logError("CrewHours: load failed", err);
        setError(err instanceof Error ? err.message : "Could not load crew intervals");
        setEvents([]);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    onCleanup(() => controller.abort());
  });

  createEffect(() => {
    const projectId = selectedProjectId();
    const sourceId = selectedSourceId();
    const sourceName = selectedSourceName();
    const loaded = events();
    if (loading()) return;
    const key = `${projectId}:${sourceId}:${sourceName}`;
    if (appliedRangeKey === key) return;
    appliedRangeKey = key;
    const dates = loaded
      .filter((event) => event.sources.some((name) => name.trim().toLowerCase() === sourceName.trim().toLowerCase()))
      .map((event) => event.date)
      .filter(Boolean)
      .sort();
    setStartDate(dates[0] ?? "");
    setEndDate(dates[dates.length - 1] ?? "");
  });

  const rangeInverted = createMemo(() => {
    const start = startDate().trim();
    const end = endDate().trim();
    return Boolean(start && end && start > end);
  });

  const summary = createMemo(() =>
    summarizeCrewHours(events(), {
      sourceName: selectedSourceName(),
      startDate: startDate(),
      endDate: endDate(),
      mode: mode(),
    })
  );

  const seatTotal = (key: CrewHourSeatKey) => summary().seatTotals[key];

  const canExport = createMemo(
    () => summary().rows.length > 0 || summary().byCrew.length > 0
  );

  const handleExportExcel = async () => {
    if (exporting() || !canExport()) return;
    setExporting(true);
    setExportError("");
    const result = await exportCrewHoursExcel({
      summary: summary(),
      filter: {
        sourceName: selectedSourceName(),
        startDate: startDate(),
        endDate: endDate(),
        mode: mode(),
      },
      sourceName: selectedSourceName(),
    });
    if (!result.success) {
      setExportError(result.message || "Export failed");
      logError("CrewHours: Excel export failed", result.message);
    } else {
      logDebug("CrewHours: Excel exported", {
        intervals: summary().intervalCount,
        sailors: summary().rows.length,
      });
    }
    setExporting(false);
  };

  return (
    <div id="media-container" class="crew-hours-page">
      <div class="crew-hours-scroll">
        <header class="crew-hours-header">
          <h1 class="crew-hours-title">CREW HOURS</h1>
          <p class="crew-hours-source">{selectedSourceName() || "No boat selected"}</p>
        </header>

        <div class="crew-hours-toolbar">
          <label class="crew-hours-field">
            <span>Start</span>
            <input
              type="date"
              value={startDate()}
              onInput={(e) => setStartDate(e.currentTarget.value)}
            />
          </label>
          <label class="crew-hours-field">
            <span>End</span>
            <input
              type="date"
              value={endDate()}
              onInput={(e) => setEndDate(e.currentTarget.value)}
            />
          </label>
          <div class="crew-hours-modes" role="group" aria-label="Racing or training">
            <For each={MODES}>
              {(item) => (
                <button
                  type="button"
                  class="crew-hours-mode"
                  classList={{ "is-active": isMode(item.id) }}
                  onClick={() => setMode(item.id)}
                >
                  {item.label}
                </button>
              )}
            </For>
          </div>
        </div>

        <Show when={selectedSourceId() > 0} fallback={<p class="crew-hours-empty">Select a boat to see crew hours.</p>}>
          <Show when={!loading()} fallback={<Loading message="Loading crew hours" compact />}>
            <Show when={!error()} fallback={<p class="crew-hours-empty">{error()}</p>}>
              <Show when={!rangeInverted()} fallback={<p class="crew-hours-empty">Start date is after the end date.</p>}>
                <p class="crew-hours-disclaimer">
                  For the 2026 season, crew hours in RaceSight were not fully tracked during training periods, so these
                  totals represent known crew hours based on the published race crew.
                </p>
                <div class="crew-hours-body">
                  <p class="crew-hours-summary">
                    {summary().intervalCount === 0
                      ? "No crew intervals in this range."
                      : `${formatHours(summary().wallHours)} h across ${summary().intervalCount} interval${summary().intervalCount === 1 ? "" : "s"}. Hours are the time each person was on the boat.`}
                  </p>
                  <Show when={summary().unnumbered > 0}>
                    <p class="crew-hours-note">
                      {summary().unnumbered} interval{summary().unnumbered === 1 ? "" : "s"} in this range
                      {summary().unnumbered === 1 ? " has" : " have"} no race number and {summary().unnumbered === 1 ? "is" : "are"} included only in All.
                    </p>
                  </Show>

                  <div class="crew-hours-panels">
                    <section class="crew-hours-section crew-hours-section--sailors">
                      <h2>By sailor and seat</h2>
                      <div class="crew-hours-table-wrap">
                        <table class="data-table compact crew-hours-table">
                          <thead>
                            <tr>
                              <th class="crew-hours-name">Sailor</th>
                              <For each={CREW_HOUR_SEATS}>
                                {(seat) => (
                                  <th class="crew-hours-seat" title={seat.label}>
                                    {seat.short}
                                  </th>
                                )}
                              </For>
                              <th class="crew-hours-seat">Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            <Show
                              when={summary().rows.length > 0}
                              fallback={
                                <tr>
                                  <td class="crew-hours-empty-cell" colSpan={CREW_HOUR_SEATS.length + 2}>
                                    No sailors in this range.
                                  </td>
                                </tr>
                              }
                            >
                              <For each={summary().rows}>
                                {(row) => (
                                  <tr>
                                    <td class="crew-hours-name">{row.sailor}</td>
                                    <For each={CREW_HOUR_SEATS}>
                                      {(seat) => <td class="crew-hours-num">{formatHours(row.hours[seat.key])}</td>}
                                    </For>
                                    <td class="crew-hours-num">{formatHours(row.total)}</td>
                                  </tr>
                                )}
                              </For>
                              <tr class="crew-hours-total-row">
                                <td class="crew-hours-name">Seat occupied</td>
                                <For each={CREW_HOUR_SEATS}>
                                  {(seat) => <td class="crew-hours-num">{formatHours(seatTotal(seat.key))}</td>}
                                </For>
                                <td class="crew-hours-num" />
                              </tr>
                            </Show>
                          </tbody>
                        </table>
                      </div>
                    </section>

                    <section class="crew-hours-section crew-hours-section--crews">
                      <h2>By crew</h2>
                      <div class="crew-hours-table-wrap">
                        <table class="data-table compact crew-hours-table crew-hours-table--crew">
                          <thead>
                            <tr>
                              <th class="crew-hours-name">Crew</th>
                              <th class="crew-hours-seat">Intervals</th>
                              <th class="crew-hours-seat">Hours</th>
                            </tr>
                          </thead>
                          <tbody>
                            <Show
                              when={summary().byCrew.length > 0}
                              fallback={
                                <tr>
                                  <td class="crew-hours-empty-cell" colSpan={3}>
                                    No crews in this range.
                                  </td>
                                </tr>
                              }
                            >
                              <For each={summary().byCrew}>
                                {(row) => (
                                  <tr>
                                    <td class="crew-hours-name">{row.code}</td>
                                    <td class="crew-hours-num">{row.intervals}</td>
                                    <td class="crew-hours-num">{formatHours(row.hours)}</td>
                                  </tr>
                                )}
                              </For>
                            </Show>
                          </tbody>
                        </table>
                      </div>
                    </section>
                  </div>

                  <div class="crew-hours-export">
                    <button
                      type="button"
                      class="btn crew-hours-export-btn"
                      disabled={exporting() || !canExport()}
                      onClick={() => void handleExportExcel()}
                    >
                      {exporting() ? "Exporting…" : "Export Excel"}
                    </button>
                    <Show when={exportError()}>
                      <span class="crew-hours-export-error">{exportError()}</span>
                    </Show>
                  </div>
                </div>
              </Show>
            </Show>
          </Show>
        </Show>
      </div>
    </div>
  );
}
